import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Box, BrandLockup, Button, Card, CardContent, Stack, Text } from '@citibox-panel/ui';
import { resolveMarketIndicator } from '@/lib/marketIndicator';
import { readRuntimeConfig } from '@/lib/runtimeConfig';

type SignOut = () => Promise<void>;

export type AccessStateScreenProps =
  | { variant: 'loading' }
  | { variant: 'no-access'; email: string | null; onSignOut: SignOut }
  | { variant: 'denied'; email: string | null; homeHref?: `/${string}`; onSignOut: SignOut }
  | {
      variant: 'error';
      onRetry: () => void;
      retrying: boolean;
      failures: number;
      onSignOut: SignOut;
    }
  | { variant: 'session-expired'; onSignOut: SignOut };

type StateBodyProps = Exclude<AccessStateScreenProps, { variant: 'loading' }>;

// TODO: i18n — copy en ES hasta Fase 1.
const ASK_FOR_ACCESS = 'Si necesitas acceso, pídeselo a tu responsable.';
const CHECK_FAILED = 'No pudimos comprobar tu acceso';

const TITLES: Record<StateBodyProps['variant'], string> = {
  'no-access': 'No tienes acceso al panel',
  denied: 'No tienes acceso a esta vista',
  error: CHECK_FAILED,
  'session-expired': CHECK_FAILED,
};

// Every full-screen access state of an authenticated session: one card, framed like the login, never
// like a crash. Rendered inside the layout's main landmark, so it adds none of its own.
export function AccessStateScreen(props: AccessStateScreenProps): ReactElement {
  const indicator = resolveMarketIndicator(readRuntimeConfig());
  return (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card variant="outlined" sx={{ minWidth: 360, maxWidth: 480, width: '100%' }}>
        <CardContent>
          <Stack spacing={3}>
            {/* The market matters here: no access in UK is not no access in ES. */}
            <BrandLockup label={indicator.text} degraded={indicator.degraded} />
            {props.variant === 'loading' ? (
              <Box role="status">
                {/* TODO: i18n */}
                <Text variant="body1">Cargando…</Text>
              </Box>
            ) : (
              <StateBody {...props} />
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

function StateBody(props: StateBodyProps): ReactElement {
  const headingRef = useRef<HTMLElement>(null);
  const [signingOut, setSigningOut] = useState(false);
  // A new failure moves focus back to the title so its outcome is heard. The busy retry button is
  // disabled by the façade while it runs, so focus is not kept on it in between.
  const focusKey = props.variant === 'error' ? props.failures : 0;

  useEffect(() => {
    headingRef.current?.focus();
  }, [focusKey]);

  const signOut = (): void => {
    setSigningOut(true);
    void props.onSignOut().catch(() => {
      setSigningOut(false);
    });
  };

  const signOutButton = (primary: boolean): ReactElement => (
    <Button
      variant={primary ? 'contained' : 'outlined'}
      color={primary ? 'primary' : 'neutral'}
      loading={signingOut}
      onClick={signOut}
    >
      {/* TODO: i18n */}
      Cerrar sesión
    </Button>
  );

  return (
    <>
      {/* Text exposes no ref or tabIndex, so the Box is the focusable h1 and carries the h5 scale
          itself: a span inside it would sit in the h1's own 2em line box and make the title taller. */}
      <Box
        component="h1"
        ref={headingRef}
        tabIndex={-1}
        sx={{ typography: 'h5', color: 'text.primary', m: 0 }}
      >
        {TITLES[props.variant]}
      </Box>
      {props.variant === 'no-access' && (
        <>
          <AccountLine email={props.email} />
          <Text variant="body1">{ASK_FOR_ACCESS}</Text>
          <Actions>{signOutButton(true)}</Actions>
          <Text variant="caption" color="muted">
            {/* TODO: i18n */}
            Para entrar con otra cuenta, cierra sesión.
          </Text>
        </>
      )}
      {props.variant === 'denied' && (
        <>
          {props.homeHref === undefined && <AccountLine email={props.email} />}
          <Text variant="body1">{ASK_FOR_ACCESS}</Text>
          <Actions>
            {props.homeHref !== undefined ? (
              <>
                <Button variant="contained" href={props.homeHref}>
                  {/* TODO: i18n */}
                  Ir al inicio
                </Button>
                {signOutButton(false)}
              </>
            ) : (
              signOutButton(true)
            )}
          </Actions>
        </>
      )}
      {props.variant === 'error' && (
        <>
          <Text variant="body1">
            {/* TODO: i18n */}
            Puede ser un problema de conexión o del servidor. Vuelve a intentarlo en unos segundos.
          </Text>
          <Actions>
            <Button variant="contained" loading={props.retrying} onClick={props.onRetry}>
              {/* TODO: i18n */}
              Reintentar
            </Button>
            {signOutButton(false)}
          </Actions>
        </>
      )}
      {props.variant === 'session-expired' && (
        <>
          <Text variant="body1">
            {/* TODO: i18n */}
            Tu sesión ya no es válida. Cierra sesión y vuelve a entrar.
          </Text>
          <Actions>{signOutButton(true)}</Actions>
        </>
      )}
    </>
  );
}

// Never truncated: the address is what exposes a sign-in with the wrong Google account.
function AccountLine({ email }: { email: string | null }): ReactElement | null {
  if (email === null) return null;
  return (
    <Box sx={{ overflowWrap: 'anywhere' }}>
      <Text variant="body1">
        {/* TODO: i18n */}
        Has entrado como{' '}
        <Text variant="body1" component="span" bold>
          {email}
        </Text>
        .
      </Text>
    </Box>
  );
}

function Actions({ children }: { children: ReactNode }): ReactElement {
  return (
    <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
      {children}
    </Stack>
  );
}
